/**
 * Client for the staff cache-observability API (`/api/staff/cache/`).
 *
 * Read-only. Two hooks backing the Cache page: aggregate `stats` and a
 * filterable/paged `keys` view. Both are registered as `LIST_LIVE` +
 * `NEVER_PERSIST` (see query-defaults.ts / persist-policy.ts) - this is
 * volatile cross-tenant operational data that must never touch IndexedDB.
 *
 * The backend fails open, so every response carries `available`; a `false`
 * value (non-Redis backend or unreachable Redis) drives a clean empty state
 * rather than an error.
 */

import { useQuery } from '@tanstack/react-query'

import api from '@/admin/lib/api'

export interface CacheNamespaceStat {
  namespace: string
  data: number
  version: number
}

export interface CacheTenantStat {
  tenant_id: string
  tenant_name: string | null
  keys: number
}

export interface CacheStats {
  available: boolean
  backend: string
  reason?: string
  dbsize?: number
  used_memory?: number
  maxmemory?: number
  used_memory_ratio?: number | null
  keyspace_hits?: number
  keyspace_misses?: number
  hit_rate?: number | null
  evicted_keys?: number
  namespaces?: CacheNamespaceStat[]
  tenants?: CacheTenantStat[]
  approximate?: boolean
  scanned?: number
}

export interface CacheKeyRow {
  kind: 'data' | 'version'
  namespace: string
  tenant_id: string
  tenant_name: string | null
  entity_type: string | null
  object_id: string | null
  version: number | null
  variant: string | null
  ttl_seconds: number | null
  size_bytes: number | null
}

export interface CacheKeysResponse {
  available: boolean
  backend: string
  reason?: string
  rows: CacheKeyRow[]
  next_cursor: string
  truncated: boolean
  scanned: number
}

export interface CacheKeysParams {
  tenant?: string
  namespace?: string
  entity_type?: string
  kind?: string
  search?: string
  withSize?: boolean
  limit?: number
}

export function useCacheStats() {
  return useQuery<CacheStats>({
    queryKey: ['staff-cache-stats'],
    queryFn: () => api.get('/staff/cache/stats/').then((r) => r.data as CacheStats),
  })
}

export function useCacheKeys(params: CacheKeysParams, enabled = true) {
  return useQuery<CacheKeysResponse>({
    queryKey: ['staff-cache-keys', params],
    enabled,
    queryFn: () => {
      const query: Record<string, string> = {}
      if (params.tenant) query.tenant = params.tenant
      if (params.namespace) query.namespace = params.namespace
      if (params.entity_type) query.entity_type = params.entity_type
      if (params.kind) query.kind = params.kind
      if (params.search) query.search = params.search
      if (params.withSize) query.with_size = '1'
      if (params.limit) query.limit = String(params.limit)
      return api
        .get('/staff/cache/keys/', { params: query })
        .then((r) => r.data as CacheKeysResponse)
    },
  })
}
